import {AxiosError} from "axios";
import {useState} from "react";
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {CRLApi, X509CRLDetails} from "../api/admin/generated";
import {DialogCloseCause} from "../common/Enums";
import {RenderAlert} from "../common/RenderAlert";
import {logger} from "../utils/Logger";
import {Utils} from "../utils/Utils";

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface CRLImportDialogProps {
    onClose : OnClose
}

/*
 * REST api for accessing the back-end
 */
const crlApi = new CRLApi(ApiConfig.config());

/**
 * Represents a dialog for importing CRLs.
 * @param {Function} onClose - The function to be called when the dialog is closed.
 * @returns {JSX.Element} The imported CRLs dialog.
 */
export const CRLImportDialog = ({onClose} : CRLImportDialogProps ) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The files to import
     */
    const [file, setFile] = useState<File>();
    /**
     * Skip expired CRLs
     */
    const [skipExpired, setSkipExpired] = useState<boolean>(true);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Set to true while importing to show a spinner on the import button
     */
    const [importing, setImporting] = useState<boolean>(false);

    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    const importCRLs = () =>
    {
        setImporting(true);

        crlApi.cRLControllerImportCRLs(file, skipExpired)
            .then((response ) =>
            {
                const imported : X509CRLDetails[] = response.data;

                if (imported.length > 0) {
                    onClose(DialogCloseCause.Succes);
                }
                else {
                    setErrorMessage(t("crls.no-new-crls-imported"));
                }
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error importing CRLs", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setImporting(false);
            });
    }

    // called when the dialog is closed without clicking export
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    return (
        <Modal show={true} onHide={() => onHideDialog()} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t("crls.import-crls")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>{t("crls.crl-file")}</Form.Label>
                        <Form.Control
                            size="sm"
                            type="file"
                            accept=""
                            onChange={e => setFile(((e.target as HTMLInputElement).files as FileList)[0])}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="switch"
                            label={t("crls.skip-expired-crls")}
                            checked={skipExpired}
                            onChange={(e) => setSkipExpired(e.target.checked)}
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={importCRLs}
                        disabled={file === undefined}>
                    {importing && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {importing ? t("crls.importing") : t("crls.import")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
